import { format, parse } from 'date-fns'
import { z } from 'zod'

export type WithId<T> = T & { id: string }

type Timestamp = { toDate: () => Date }
type SerializedTimestamp = { _seconds: number; _nanoseconds?: number }
type SerializedTimestampAlt = { seconds: number; nanoseconds?: number }

// Firestore Timestamp schema
export const FirestoreTimestamp = z.preprocess((val) => {
  if (val instanceof Date) {
    return val
  }

  // Firestore Timestamp object (has toDate method)
  if (
    typeof val === 'object' &&
    val !== null &&
    'toDate' in val &&
    typeof (val as Timestamp).toDate === 'function'
  ) {
    return (val as Timestamp).toDate()
  }

  if (
    typeof val === 'object' &&
    val !== null &&
    '_seconds' in val &&
    typeof (val as SerializedTimestamp)._seconds === 'number'
  ) {
    return new Date((val as SerializedTimestamp)._seconds * 1000)
  }

  if (
    typeof val === 'object' &&
    val !== null &&
    'seconds' in val &&
    typeof (val as SerializedTimestampAlt).seconds === 'number'
  ) {
    return new Date((val as SerializedTimestampAlt).seconds * 1000)
  }

  if (typeof val === 'string') {
    const date = new Date(val)
    if (!Number.isNaN(date.getTime())) {
      return date
    }
  }

  return val
}, z.custom<Date>())

// ISO Date utilities (YYYY-MM-DD format)
// Using z.iso.date() which validates YYYY-MM-DD format
const IsoDateSchema = z.iso.date()

/**
 * Validates that a string is a valid ISO date in YYYY-MM-DD format
 * @param dateString - String to validate
 * @returns true if valid ISO date, false otherwise
 * @example
 * isValidIsoDate('2025-01-15') // true
 * isValidIsoDate('2025-1-15') // false (not zero-padded)
 * isValidIsoDate('2025-13-01') // false (invalid month)
 */
export function isValidIsoDate(dateString: unknown): dateString is string {
  return IsoDateSchema.safeParse(dateString).success
}

/**
 * Parses an ISO date string (YYYY-MM-DD) to a JavaScript Date object
 * Uses date-fns parse() for reliable parsing at local midnight
 * @param isoDateString - ISO date string to parse
 * @returns Date object set to midnight local time
 * @throws Error if the string is not a valid ISO date format
 * @example
 * parseIsoDate('2025-01-15') // Date object for Jan 15, 2025
 * parseIsoDate('2025-1-15') // throws Error
 */
export function parseIsoDate(isoDateString: string): Date {
  const result = IsoDateSchema.safeParse(isoDateString)
  if (!result.success) {
    throw new Error(
      `Invalid ISO date string: ${isoDateString}. Expected format: YYYY-MM-DD`,
    )
  }
  // Parse as local date at midnight to avoid timezone issues
  return parse(result.data, 'yyyy-MM-dd', new Date())
}

/**
 * Safely parses an ISO date string, returning null if invalid
 * Uses date-fns parse() for reliable parsing at local midnight
 * @param isoDateString - ISO date string to parse
 * @returns Date object or null if invalid
 * @example
 * safeParseIsoDate('2025-01-15') // Date object
 * safeParseIsoDate('invalid') // null
 */
export function safeParseIsoDate(isoDateString: string): Date | null {
  const result = IsoDateSchema.safeParse(isoDateString)
  if (!result.success) {
    return null
  }
  // Parse as local date at midnight to avoid timezone issues
  return parse(result.data, 'yyyy-MM-dd', new Date())
}

/**
 * Converts a Date object to ISO date string (YYYY-MM-DD)
 * Uses date-fns for consistent formatting
 * @param date - Date object to convert
 * @returns ISO date string in YYYY-MM-DD format
 * @example
 * toIsoDateString(new Date(2025, 0, 15)) // '2025-01-15'
 */
export function toIsoDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

// Matches "HH:MM" or "HH:MM:SS", optionally followed by AM/PM
// (with or without spaces / periods, case-insensitive).
const TIME_WITH_COLON =
  /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/i

// Matches "11am" / "1 pm" etc. — hour-only is only accepted when the
// meridiem is present, otherwise "11" alone would be ambiguous
// (11:00 AM vs 11:00 the 24-hour, vs incomplete typing of "1130").
const TIME_HOUR_ONLY_WITH_MERIDIEM = /^(\d{1,2})\s*(am|pm|a\.m\.|p\.m\.)$/i

/**
 * Parses a user-entered time string and normalizes it to canonical 24-hour
 * "HH:MM" form. Accepts:
 * - 24-hour: "11:30", "23:30", "11:30:45"
 * - 12-hour with minutes: "11:30 AM", "11:30 pm", "11:30 a.m.", "11:30am"
 * - 12-hour hour-only: "11 AM", "11pm", "9 PM" (meridiem is required here
 *   so a bare "11" stays ambiguous and is rejected)
 *
 * Returns null when the input cannot be parsed as a valid time.
 */
export function parseTimeString(input: string): string | null {
  const trimmed = input.trim()
  let hours: number
  let minutes: number
  let meridiem: string | undefined

  const colon = TIME_WITH_COLON.exec(trimmed)
  if (colon) {
    hours = Number(colon[1])
    minutes = Number(colon[2])
    const seconds = colon[3] !== undefined ? Number(colon[3]) : 0
    if (seconds >= 60) return null
    meridiem = colon[4]?.toLowerCase().replace(/\./g, '')
  } else {
    const hourOnly = TIME_HOUR_ONLY_WITH_MERIDIEM.exec(trimmed)
    if (!hourOnly) return null
    hours = Number(hourOnly[1])
    minutes = 0
    meridiem = hourOnly[2].toLowerCase().replace(/\./g, '')
  }

  if (minutes >= 60) return null

  if (meridiem) {
    if (hours < 1 || hours > 12) return null
    if (meridiem === 'am' && hours === 12) hours = 0
    else if (meridiem === 'pm' && hours !== 12) hours += 12
  } else if (hours > 23) {
    return null
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Formats a time string for display in the friendly 12-hour form, e.g.
 * "11:30 AM". Pass either a canonical "HH:MM" or anything parseTimeString
 * accepts. If the input can't be parsed, it's returned unchanged so an
 * in-flight error message can describe what went wrong.
 */
export function formatTimeForDisplay(input: string): string {
  const canonical = parseTimeString(input)
  if (canonical === null) return input

  const [hStr, mStr] = canonical.split(':')
  const hours = Number(hStr)
  const minutes = Number(mStr)
  const meridiem = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  return `${hour12}:${String(minutes).padStart(2, '0')} ${meridiem}`
}

/**
 * Time-of-day schema. Accepts 24-hour and 12-hour input from users (e.g. the
 * HTML `<input type="time">` control degrades to a free-form text input on
 * some browsers), and normalizes to canonical 24-hour "HH:MM".
 *
 * Replaces `z.iso.time()` at form/API boundaries where humans type values —
 * MCP-620.
 */
export const TimeString = z
  .string()
  .trim()
  .transform((val, ctx) => {
    const normalized = parseTimeString(val)
    if (normalized === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a valid time (e.g. 11:30 AM or 14:30)',
      })
      return z.NEVER
    }
    return normalized
  })

// Address schema
export const Address = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  country: z.string().optional(),
})
